import React from 'react';
import styled from 'styled-components';
import JSON5 from 'json5';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactFileRender from 'shared/design-system/components/TrueImpactFileRender';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { ReportAnswers } from '../FeedbackReport';
import { renderTextWithParagraphs } from './ReportNarrative';

interface ReportSectionNoteProps {
  answers: ReportAnswers;
  questionId: number;
  label: string;
  className?: string;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  padding: ${SPACERS.medium} ${SPACERS.extraLarge};
  .note-files {
    margin-top: ${SPACERS.small};
  }
`;

const parseNote = (
  text: string | undefined,
): { description: string; files: TrueImpactFile[] } => {
  if (!text) return { description: '', files: [] };
  try {
    const parsed = JSON5.parse(text);
    return {
      description: parsed.description || '',
      files: Array.isArray(parsed.files) ? parsed.files : [],
    };
  } catch (e) {
    return { description: text, files: [] };
  }
};

const findNoteAnswer = (answers: ReportAnswers, questionId: number) => {
  for (const section of Object.values(answers)) {
    const match = section?.find(a => a.question.dbId === questionId);
    if (match) return match;
  }
  return undefined;
};

const ReportSectionNote: React.FC<ReportSectionNoteProps> = ({
  answers,
  questionId,
  label,
  className,
}) => {
  const answer = findNoteAnswer(answers, questionId);
  const { description, files } = parseNote(answer?.text);
  if (!description && files.length === 0) return null;
  return (
    <StyledDiv className={className}>
      <TrueImpactText variant="heading-label">{label}</TrueImpactText>
      {description && (
        <TrueImpactText variant="body-small">
          {renderTextWithParagraphs(description)}
        </TrueImpactText>
      )}
      {files.map((f, i) => (
        <TrueImpactFileRender key={i} file={f} className="note-files" />
      ))}
    </StyledDiv>
  );
};

export default ReportSectionNote;
